import React from "react";
import SEO from "./SEO";
import Showroom from "./Showroom";
import ContactUs from "./ContactUs";
import BackgroundImg from "../assets/background.jpg";
import logo from "../assets/yamaha-logo.png";
import { Helmet } from "react-helmet-async";
import TestDriveButton from "./TestDriveButton";
// duplicate import removed

const UttamNagarShowroom = () => {
  // Localized JSON-LD Schema markup for Uttam Nagar audience
  const uttamNagarSchema = {
    "@context": "https://schema.org",
    "@type": "AutoDealer",
    "name": "Excellent Yamaha Showroom - Serving Uttam Nagar",
    "image": "https://excellentmotors.in/favicon.png",
    "telephone": "+91 7982920138",
    "url": "https://excellentmotors.in/yamaha-showroom-uttam-nagar",
    "description": "Authorized Yamaha showroom and dealer serving Uttam Nagar, Dwarka, Janakpuri, and West Delhi. Explore FZ, MT-15, R15 V4, and Aerox 155 models with easy financing.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "RR-11, near Peeragarhi metro station, Paschim Vihar (Closest Branch for Uttam Nagar)",
      "addressLocality": "New Delhi",
      "addressRegion": "Delhi",
      "postalCode": "110087",
      "addressCountry": "IN"
    }
  };

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      <Helmet>
        <title>Authorized Yamaha Showroom near Uttam Nagar | West Delhi Dealer</title>
        <meta name="description" content="Looking for an authorized Yamaha dealer near Uttam Nagar, Dwarka, or Janakpuri? Excellent Motors is West Delhi's premier Yamaha partner. Get exchange bonus & low down-payments!" />
        <link rel="canonical" href="https://excellentmotors.in/yamaha-showroom-uttam-nagar" />
      </Helmet>
      <SEO
        title="Authorized Yamaha Showroom near Uttam Nagar | West Delhi Dealer"
        description="Looking for an authorized Yamaha dealer near Uttam Nagar, Dwarka, or Janakpuri? Excellent Motors is West Delhi's premier Yamaha partner. Get exchange bonus & low down-payments!"
        keywords="Yamaha showroom near Uttam Nagar, Yamaha showroom Dwarka, Yamaha Janakpuri, Yamaha showroom West Delhi, Yamaha MT-15 Uttam Nagar, Yamaha R15 Uttam Nagar"
        canonical="https://excellentmotors.in/yamaha-showroom-uttam-nagar"
        schema={uttamNagarSchema}
      />

      {/* Uttam Nagar Localized Hero Section */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden border-b border-gray-800">
        {/* Background Image layer with 30% opacity */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30 z-0"
          style={{ backgroundImage: `url(${BackgroundImg})` }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-gray-900/90 to-blue-950/95 z-0"></div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-6">
          <img src={logo} alt="Yamaha R-DNA Logo" className="h-16 w-auto mx-auto mb-4 animate-bounce" />
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-wide uppercase text-white leading-tight">
            Authorized Yamaha Showroom <br className="hidden sm:inline" />
            <span className="text-yellow-400">Serving Uttam Nagar & West Delhi</span>
          </h1>
          <p className="text-gray-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed">
            Welcome to <strong>Excellent Motors</strong>, the top choice for Uttam Nagar, Dwarka, and Janakpuri riders seeking pure Yamaha performance. We are West Delhi's premier authorized showroom, loaded with the complete range of Yamaha motorcycles, scooters, accessories, and certified after-sales care.
          </p>

          {/* Quick trust metrics */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto pt-8 text-sm text-gray-200">
            <div className="bg-gradient-to-br from-blue-900/95 via-blue-950 to-indigo-950/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Down Payment</h3>
              <p className="text-white font-medium">Starts as low as ₹10,000* on city scooters!</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-900/95 via-blue-950 to-blue-950/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Exchange Offer</h3>
              <p className="text-white font-medium">Exchange bonus up to ₹3,000 on your old ride.</p>
            </div>
            <div className="bg-gradient-to-br from-blue-950 via-indigo-950 to-blue-900/90 border-2 border-yellow-500 rounded-xl p-5 shadow-2xl transform transition duration-300 hover:scale-105 hover:shadow-yellow-500/20 text-center sm:text-left">
              <h3 className="text-yellow-400 font-extrabold text-lg mb-1.5 uppercase tracking-wider">Instant Finance</h3>
              <p className="text-white font-medium">Approval on the spot with minimum documents.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Local Advertising Copy Block */}
      <section className="py-16 px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold border-l-4 border-yellow-500 pl-3">
              Why Uttam Nagar Riders Prefer Excellent Yamaha
            </h2>
            <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
              Living in Uttam Nagar, Dwarka Mor, or Janakpuri? Excellent Motors is just a short drive away! Our showrooms are designed to offer a transparent and stress-free purchasing experience. 
            </p>
            <p className="text-gray-400 leading-relaxed text-sm sm:text-base">
              We stock West Delhi's largest inventory of hot-selling models like the <strong>Yamaha R15 V4, MT-15 V2.0, FZ-S FI Hybrid, and the aerodynamic Aerox 155 Maxi-Scooter</strong>. Get your hands on official Yamaha merchandise, riding gear, and authentic Yamaha parts under one single roof.
            </p>
            <ul className="space-y-2.5 text-sm text-gray-300 font-semibold">
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Free Test Rides available on all models</li>
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Authorized Yamaha Technical Academy service support</li>
              <li className="flex items-center"><i className="fa-solid fa-circle-check text-yellow-500 mr-2"></i> Transparent deals with zero hidden charges</li>
            </ul>
          </div>
          <div className="group bg-gradient-to-br from-blue-900 via-indigo-950 to-slate-950 border border-blue-900/40 p-8 rounded-2xl shadow-xl text-center space-y-6 transform transition-all duration-300 hover:shadow-blue-500/10">
            <div className="relative h-44 w-full overflow-hidden rounded-xl bg-gray-950 shadow-inner">
              <img 
                src="/images/mt03.jpg" 
                alt="Yamaha Hyper Naked MT-03 Showcase" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/30 to-transparent"></div>
              <div className="absolute bottom-3 left-0 right-0">
                <span className="text-yellow-400 text-xs font-black uppercase tracking-widest bg-blue-950/85 px-3 py-1 rounded-full border border-yellow-500/30">
                  MT Series Hyper Naked
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-extrabold text-white uppercase tracking-wide">Book Your Dream Yamaha Today</h3>
              <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
                Don't wait for your ride. Reserve your test ride or get custom EMI quotes directly from our relationship manager!
              </p>
            </div>
            <a 
              href="#contact" 
              className="bg-yellow-500 hover:bg-yellow-400 text-blue-950 font-extrabold px-6 py-3 rounded-xl inline-block w-full text-center transition transform active:scale-95 shadow-md hover:shadow-yellow-500/20"
            >
              Inquire Now &rarr;
            </a>
          </div>
        </div>
      </section>

      {/* Embedded Showrooms Location tag block */}
      <section className="bg-gray-950/80 py-4 border-t border-b border-gray-850">
        <Showroom />
      </section>

      {/* Embedded Contact Us Form */}
              <ContactUs />
        <TestDriveButton />
    </div>
  );
};

export default UttamNagarShowroom;
